import SubtractAlt16 from '@carbon/icons-react/es/subtract--alt/16';
import { Col, Form, Row } from 'antd';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Select from 'react-select';
import styled from 'styled-components';
import { HubspotSyncTypes } from 'api/integrations';
import { Button } from 'components/atoms/button';
import { Popconfirm } from 'components/atoms/popconfirm';
import { SingleValue, Option } from 'components/atoms/select';
import { useTranslate } from 'hooks/use-translation';
import { useHubspotMappableNativeResourceFields } from 'queries/hubspot-integration';
import {
  NativeFieldExtra,
  PartialHubspotFieldMappingLocalData,
  HubspotFieldMappingLocalData,
  HubspotResourceMappingLocalData,
} from '../types';
import { PopoverSelect } from './commons/popover-select';
import {
  SelectedSyncValue,
  SyncOptionLabel,
  SyncSelectStyles,
  getValidSyncOptions,
} from './commons/sync-utils';
import { syncOptions } from './use-field-mapping-actions';

const InlineForm = styled(Form).attrs({ layout: 'inline' })`
  width: 100%;
`;

const Container = styled.div`
  padding: 0px 16px;
`;

type FieldMappingType = {
  integrationId: number;
  hubspotFieldValue: string;
  fieldMap: HubspotFieldMappingLocalData;
  externalFields: Option[];
  onRemove: () => void;
  onChange: (
    resourceId: number,
    mappingId: string,
    payload: PartialHubspotFieldMappingLocalData,
  ) => void;
  fieldObject: HubspotResourceMappingLocalData;
};

export const FieldMapping = ({
  integrationId,
  hubspotFieldValue,
  fieldObject,
  fieldMap,
  externalFields,
  onRemove,
  onChange,
}: FieldMappingType) => {
  const isCreatingMapping = useMemo(() => {
    return Boolean(
      fieldMap.id &&
        fieldMap.hubspotField &&
        fieldMap.rocketlaneField &&
        fieldMap.syncField &&
        fieldMap.fieldMappingConfigId == null,
    );
  }, [fieldMap]);

  const [getFields, setGetFields] = useState(false);
  const translate = useTranslate();

  const handleSyncOptionChange = (selected: SingleValue) => {
    onChange?.(fieldObject.resourceMappingConfigId, fieldMap.id, {
      ...fieldMap,
      syncField: (selected?.value as HubspotSyncTypes) ?? undefined,
    });
  };

  const { data, isLoading } = useHubspotMappableNativeResourceFields(
    integrationId,
    'project',
    fieldObject.externalResourceType,
    hubspotFieldValue,
    {
      enabled:
        !!fieldObject.externalResourceType && !!hubspotFieldValue && getFields,
    },
  );

  const handleNativeFieldChange = (value: SingleValue<NativeFieldExtra>) => {
    onChange?.(fieldObject.resourceMappingConfigId, fieldMap.id, {
      ...fieldMap,
      rocketlaneField: value ?? undefined,
    });
  };

  const handleExternalFieldChange = (selectedValue: SingleValue<boolean>) => {
    let temp: any;

    if (selectedValue) {
      temp = {
        ...selectedValue,
        label:
          selectedValue?.parent === fieldObject.externalResourceType
            ? selectedValue?.label
            : `[${selectedValue?.parent}] ${selectedValue?.label}`,
      };
    }
    onChange?.(fieldObject.resourceMappingConfigId, fieldMap.id, {
      ...fieldMap,
      hubspotField: temp,
      rocketlaneField: undefined,
      syncField: HubspotSyncTypes.SyncExternalToNative,
    });
  };

  const projectFieldsText = translate('project_fields');
  const calculatedFieldsText = translate('calculated_fields');

  const nativeFieldAsOptions = useMemo(
    () =>
      (data ?? []).map(
        (field) =>
          ({
            value: field.integrationFieldId,
            label: field.fieldLabel ?? field.fieldName,
            parent: field.parentFieldName
              ? field.parentFieldName === 'meta_data'
                ? projectFieldsText
                : field.parentFieldName
              : calculatedFieldsText,
            extra: {
              isEditable: field.editableByFieldMapping,
              fieldName: field.fieldName,
            },
          } as Option<NativeFieldExtra>),
      ),
    [data, projectFieldsText, calculatedFieldsText],
  );

  const availableSyncOptions = useMemo(
    () => getValidSyncOptions(fieldMap),
    [fieldMap],
  );

  return (
    <Container>
      <Row>
        <InlineForm>
          <Col flex="350px">
            <Form.Item>
              <PopoverSelect
                loading={isCreatingMapping}
                onChange={handleExternalFieldChange}
                value={fieldMap?.hubspotField}
                options={externalFields}
                type="hubspot"
                fieldObject={fieldObject}
              />
            </Form.Item>
          </Col>

          <Col flex="62px">
            <Form.Item hidden={!fieldMap?.hubspotField}>
              <Select
                isSearchable={false}
                styles={SyncSelectStyles}
                onChange={(val) => handleSyncOptionChange(val)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                formatOptionLabel={SyncOptionLabel}
                options={availableSyncOptions}
                isDisabled={availableSyncOptions.length === 0}
                value={syncOptions.find(
                  (option) => option.value === fieldMap?.syncField,
                )}
                defaultValue={syncOptions.find(
                  (option) => option.value === fieldMap?.syncField,
                )}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorsContainer: () => null,
                  IndicatorSeparator: () => null,
                  SingleValue: SelectedSyncValue,
                }}
                isLoading={isCreatingMapping}
              />
            </Form.Item>
          </Col>

          <Col flex="408px">
            <Form.Item hidden={!fieldMap?.hubspotField}>
              <PopoverSelect
                onVisibilityChange={setGetFields}
                onChange={handleNativeFieldChange}
                value={fieldMap?.rocketlaneField}
                options={nativeFieldAsOptions}
                loading={isLoading || isCreatingMapping}
                triggerLabel={
                  <FormattedMessage id="settings.integrations.sf.resource_mappings.rocketlane.trigger" />
                }
                type="rocketlane"
                fieldObject={fieldObject}
              />
            </Form.Item>
          </Col>

          <Col flex="auto">
            <Popconfirm
              onConfirm={onRemove}
              subTitle={
                <FormattedMessage id="settings.integrations.sf.mapping.delete" />
              }
            >
              <Button
                icon={<SubtractAlt16 style={{ color: 'var(--gray-50)' }} />}
                type="text"
                loading={isCreatingMapping}
              />
            </Popconfirm>
          </Col>
        </InlineForm>
      </Row>
    </Container>
  );
};
